/**
 * YCJ Countdown Banner JavaScript
 * Professional countdown timer with enhanced features
 */
class YCJCountdownBanner {
    constructor(element) {
        this.element = element;
        this.timer = element.querySelector('.ycj-countdown__timer');
        this.interval = null;
        this.isFinished = false;

        // Get configuration from data attributes
        this.config = {
            epoch: Number(this.timer?.dataset.epoch || 0) * 1000,
            finishText: this.timer?.dataset.finish || 'We are live!',
            hideAfterFinish: this.timer?.dataset.hideAfterFinish === 'true',
            showMilliseconds: this.timer?.dataset.showMilliseconds === 'true',
            updateInterval: this.timer?.dataset.updateInterval ? Number(this.timer.dataset.updateInterval) : 1000
        };

        // Cache DOM elements
        this.elements = {
            days: this.timer?.querySelector('[data-unit="days"]'),
            hours: this.timer?.querySelector('[data-unit="hours"]'),
            minutes: this.timer?.querySelector('[data-unit="minutes"]'),
            seconds: this.timer?.querySelector('[data-unit="seconds"]')
        };

        this.init();
    }

    init() {
        if (!this.timer || !this.config.epoch || isNaN(this.config.epoch)) {
            this.showError();
            return;
        }

        // Add loading state
        this.element.classList.add('ycj-countdown__loading');

        // Start the countdown
        this.tick();
        this.interval = setInterval(() => this.tick(), this.config.updateInterval);

        // Remove loading state after first tick
        setTimeout(() => {
            this.element.classList.remove('ycj-countdown__loading');
        }, 100);

        // Add event listeners
        this.addEventListeners();
    }

    tick() {
        const now = Date.now();
        const diff = Math.max(0, this.config.epoch - now);

        if (diff <= 0 && !this.isFinished) {
            this.finish();
            return;
        }

        const time = this.calculateTime(diff);
        this.updateDisplay(time);

        // Dispatch custom event for other scripts to listen to
        this.element.dispatchEvent(new CustomEvent('ycj:countdown:tick', {
            detail: { time, diff, element: this.element }
        }));
    }

    calculateTime(milliseconds) {
        const totalSeconds = Math.floor(milliseconds / 1000);

        return {
            days: Math.floor(totalSeconds / 86400),
            hours: Math.floor((totalSeconds % 86400) / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: Math.floor(totalSeconds % 60),
            milliseconds: Math.floor(milliseconds % 1000)
        };
    }

    updateDisplay(time) {
        Object.entries(this.elements).forEach(([unit, element]) => {
            if (element && time[unit] !== undefined) {
                const value = String(time[unit]).padStart(2, '0');
                if (element.textContent !== value) {
                    element.textContent = value;
                    this.animateChange(element);
                }
            }
        });
    }

    animateChange(element) {
        // Add a subtle animation when numbers change
        element.style.transform = 'scale(1.1)';
        element.style.transition = 'transform 0.2s ease';

        setTimeout(() => {
            element.style.transform = 'scale(1)';
        }, 200);
    }

    finish() {
        this.isFinished = true;
        clearInterval(this.interval);

        // Create finished message
        const finishedElement = document.createElement('div');
        finishedElement.className = 'ycj-countdown__finished';
        finishedElement.textContent = this.config.finishText;
        finishedElement.setAttribute('aria-live', 'polite');

        // Replace timer with finished message
        this.timer.replaceWith(finishedElement);

        // Dispatch finished event
        this.element.dispatchEvent(new CustomEvent('ycj:countdown:finished', {
            detail: { element: this.element, finishText: this.config.finishText }
        }));

        // Hide banner after finish if configured
        if (this.config.hideAfterFinish) {
            setTimeout(() => {
                this.element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
                this.element.style.opacity = '0';
                this.element.style.transform = 'translateY(-100%)';

                setTimeout(() => {
                    this.element.style.display = 'none';
                }, 500);
            }, 3000);
        }
    }

    showError() {
        if (this.timer) {
            this.timer.innerHTML = '<div class="ycj-countdown__error">Invalid date configuration</div>';
        }
        console.warn('YCJ Countdown Banner: Invalid or missing target datetime');
    }

    addEventListeners() {
        // Handle visibility change (pause when tab is not active)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                clearInterval(this.interval);
            } else if (!this.isFinished) {
                this.tick(); // Update immediately when tab becomes visible
                this.interval = setInterval(() => this.tick(), this.config.updateInterval);
            }
        });

        // Handle window focus/blur
        window.addEventListener('focus', () => {
            if (!this.isFinished) {
                this.tick(); // Sync time when window regains focus
            }
        });
    }

    // Public methods for external control
    destroy() {
        clearInterval(this.interval);
        this.element.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    reset(newEpoch) {
        this.isFinished = false;
        this.config.epoch = newEpoch * 1000;
        clearInterval(this.interval);
        this.init();
    }

    pause() {
        clearInterval(this.interval);
    }

    resume() {
        if (!this.isFinished) {
            this.interval = setInterval(() => this.tick(), this.config.updateInterval);
        }
    }
}

// Auto-initialize all countdown banners on page load
document.addEventListener('DOMContentLoaded', () => {
    const countdownBanners = document.querySelectorAll('[id="ycj-countdown"]');

    countdownBanners.forEach(banner => {
        // Store instance on the element for external access
        banner.ycjCountdown = new YCJCountdownBanner(banner);
    });
});

// Make class available globally for manual initialization
window.YCJCountdownBanner = YCJCountdownBanner;
